<script lang="ts">
  import { diffRenderer } from '$lib/services/DiffRenderer'
  import type { Segment } from '$lib/stores/aiFeatures'

  /**
   * StatsController: Handles statistics calculation for AI segments
   * Single Responsibility: Calculate diff statistics and counts
   */

  interface Props {
    aiSegments: Segment[]
  }

  let { 
    aiSegments = []
  }: Props = $props()

  // Calculate total diff statistics
  let totalStats = $derived.by(() => {
    let totalAdditions = 0
    let totalDeletions = 0
    
    aiSegments.forEach(segment => {
      if (segment.suggested && segment.suggested !== segment.original) {
        const stats = diffRenderer.getDiffStats(segment.original, segment.suggested)
        totalAdditions += stats.additions
        totalDeletions += stats.deletions
      }
    })
    
    return { additions: totalAdditions, deletions: totalDeletions }
  })

  // Export getters
  export function getTotalStats() {
    return totalStats
  }

  export function getTotalAdditions() {
    return totalStats.additions
  }

  export function getTotalDeletions() {
    return totalStats.deletions
  }

  // Get segment-specific stats
  export function getSegmentStats(segmentId: string) {
    const segment = aiSegments.find(s => s.id === segmentId)
    if (!segment || !segment.suggested || segment.suggested === segment.original) {
      return { additions: 0, deletions: 0 }
    }
    return diffRenderer.getDiffStats(segment.original, segment.suggested)
  }

  // Calculate summary statistics
  export function getSummaryStats() {
    const processed = aiSegments.filter(s => s.status === 'applied' || s.status === 'skipped')
    const pending = aiSegments.filter(s => s.status === 'pending')
    const applied = aiSegments.filter(s => s.status === 'applied')
    const skipped = aiSegments.filter(s => s.status === 'skipped')
    
    return {
      total: aiSegments.length,
      processed: processed.length,
      pending: pending.length,
      applied: applied.length,
      skipped: skipped.length,
      percentComplete: aiSegments.length > 0 
        ? Math.round((processed.length / aiSegments.length) * 100)
        : 0
    }
  }
</script>